import { allyJobKind, type JobKindSource } from './jobKind';

export type JobsScope = 'ALL' | 'ALLY' | 'RETAIL';
export type AllyFilter = 'ALL' | 'CLICK_INS' | 'PAVE' | 'E_CARS';
export type RetailFilter = 'ALL' | 'AUTOMOTIVE' | 'NON_AUTOMOTIVE';

export interface JobListFilter {
  scope: JobsScope;
  ally: AllyFilter;
  retail: RetailFilter;
}

export const defaultJobListFilter: JobListFilter = {
  scope: 'ALL',
  ally: 'ALL',
  retail: 'ALL',
};

export const jobScopes: JobsScope[] = ['ALL', 'ALLY', 'RETAIL'];
export const allyFilters: AllyFilter[] = ['ALL', 'CLICK_INS', 'PAVE', 'E_CARS'];
export const retailFilters: RetailFilter[] = [
  'ALL',
  'AUTOMOTIVE',
  'NON_AUTOMOTIVE',
];

/** Retail subtype chips. Non-automotive is omitted when that feature is off. */
export const retailFiltersForConfig = (
  nonAutomotiveEnabled: boolean
): RetailFilter[] =>
  nonAutomotiveEnabled
    ? retailFilters
    : retailFilters.filter((filter) => filter !== 'NON_AUTOMOTIVE');

export const sanitizeJobListFilter = (
  filter: JobListFilter,
  nonAutomotiveEnabled: boolean
): JobListFilter => {
  if (!nonAutomotiveEnabled && filter.retail === 'NON_AUTOMOTIVE') {
    return { ...filter, retail: 'ALL' };
  }
  return filter;
};

export const jobScopeLabelKey = (scope: JobsScope): string => {
  switch (scope) {
    case 'ALL':
      return 'jobs.filterAll';
    case 'ALLY':
      return 'jobs.filterAlly';
    case 'RETAIL':
      return 'jobs.filterRetail';
    default: {
      const unreachable: never = scope;
      return unreachable;
    }
  }
};

export const allyFilterLabelKey = (filter: AllyFilter): string => {
  switch (filter) {
    case 'ALL':
      return 'jobs.filterAll';
    case 'CLICK_INS':
      return 'jobs.kindClickIns';
    case 'PAVE':
      return 'jobs.kindPave';
    case 'E_CARS':
      return 'jobs.kindECars';
    default: {
      const unreachable: never = filter;
      return unreachable;
    }
  }
};

export const retailFilterLabelKey = (filter: RetailFilter): string => {
  switch (filter) {
    case 'ALL':
      return 'jobs.filterAll';
    case 'AUTOMOTIVE':
      return 'jobs.kindAutomotive';
    case 'NON_AUTOMOTIVE':
      return 'jobs.kindNonAutomotive';
    default: {
      const unreachable: never = filter;
      return unreachable;
    }
  }
};

export const jobMatchesListFilter = (
  job: JobKindSource,
  filter: JobListFilter
): boolean => {
  switch (filter.scope) {
    case 'ALL':
      return true;
    case 'ALLY': {
      const kind = allyJobKind(job);
      if (kind == null) {
        return false;
      }
      switch (filter.ally) {
        case 'ALL':
          return true;
        case 'CLICK_INS':
        case 'PAVE':
        case 'E_CARS':
          return kind === filter.ally;
        default: {
          const unreachable: never = filter.ally;
          return unreachable;
        }
      }
    }
    case 'RETAIL': {
      if (allyJobKind(job) != null) {
        return false;
      }
      switch (filter.retail) {
        case 'ALL':
          return true;
        case 'AUTOMOTIVE':
          return job.category?.isAutomotive !== false;
        case 'NON_AUTOMOTIVE':
          return job.category?.isAutomotive === false;
        default: {
          const unreachable: never = filter.retail;
          return unreachable;
        }
      }
    }
    default: {
      const unreachable: never = filter.scope;
      return unreachable;
    }
  }
};
